import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { RequestError } from '@/services/config';
import { Http } from '@/services/httpRequest';

import { type MaybeSnakeStreamSettings, mapVideoStreamSettingsValuesToDomain } from './mappers';
import type {
    UpdateLocationVideoStreamSettingsVariables,
    UseUpdateLocationVideoStreamSettingsResult,
    VideoStreamSettingsValues,
} from './types';
import { getClientVideoStreamSettingsKey } from './useGetClientVideoStreamSettings';

export const useUpdateLocationVideoStreamSettings =
    (): UseUpdateLocationVideoStreamSettingsResult => {
        const queryClient = useQueryClient();
        const mutation = useMutation<
            VideoStreamSettingsValues[],
            RequestError | Error,
            UpdateLocationVideoStreamSettingsVariables
        >({
            mutationFn: async (variables: UpdateLocationVideoStreamSettingsVariables) => {
                const { clientId, locationId, requestBody } = variables;
                const response = await Http.putRequest(
                    `/api/v2/client/internal/${clientId}/location/${locationId}/video-stream-settings`,
                    undefined,
                    requestBody,
                    undefined,
                    {
                        headers: { 'Content-Type': 'application/json' },
                    }
                );

                // The hand-written request has no generated response type; the body is the
                // location's bioscope settings, camelCase or snake_case.
                return (response.data ?? []).map((entry: MaybeSnakeStreamSettings) =>
                    mapVideoStreamSettingsValuesToDomain(entry)
                );
            },
            onSuccess: (_data, variables) => {
                void queryClient.invalidateQueries({
                    queryKey: getClientVideoStreamSettingsKey(variables.clientId),
                    exact: true,
                });
            },
        });

        return {
            ...mutation,
            isLoading: mutation.isPending,
            error: mutation.error ?? null,
        };
    };
